/**
 * Unit 2.IMPL.A — TanStack `useReactTable` orchestrator for DocumentList.
 *
 * Bead: insurescope-zkolv.1.2.3.1.
 *
 * Fat hook that owns the table instance, column composition, branch resolver,
 * and row-className helper. The consumer (`DocumentList.tsx`, rewired in
 * IMPL.D) is reduced to orchestration: render the result of this hook plus
 * the toolbar / header / body / pagination.
 *
 * Scope of IMPL.A (this file):
 *   - Table instance creation (`useReactTable` with `getRowId`,
 *     `enableColumnResizing: false`, `enableMultiSort: false`,
 *     `manualPagination`, `manualSorting`, `manualFiltering`).
 *   - `getDocumentListColumns` composition (move mode, hide-actions).
 *   - Branch discriminator (`loading | error | empty-{search,folder,library,
 *     kb-root} | populated`).
 *   - `rowClassName` helper (clickable / inert / excluded / drop-target),
 *     including the new copper outline for `library-row--drop-target` per
 *     DESIGN brief and Unit 2.RED #16d.
 *
 * Out of scope (handled by later sub-beads):
 *   - Selection wiring → IMPL.B.
 *   - Sort/filter/pagination state bridges to `useFilterModel` and the React
 *     Query key → IMPL.C.
 *   - Body rendering (skeleton, empty states, populated rows) → IMPL.D.
 *   - Density mapping + selected-row styling at the row level → IMPL.E.
 *   - Row click dispatch → IMPL.F.
 *   - Search pill + ActionsBar collapse + processing-scopes register → IMPL.G.
 */
import { useEffect, useMemo } from "react";
import {
    getCoreRowModel,
    useReactTable,
    type ColumnDef,
    type OnChangeFn,
    type PaginationState,
    type Row,
    type RowSelectionState,
    type SortingState,
    type Table,
} from "@tanstack/react-table";

import type { LibraryTab } from "@/api";

import { getDocumentListColumns } from "../components/documentListColumns";
import type { ILibraryRow } from "../components/documentListColumns.types";
import type { LibraryTypeFilter } from "../utils/libraryItemTypes";
import { libraryItemRowId } from "../utils/libraryRowId";
import { isForeignSharedSubject } from "../utils/folderTreeUtils";

export type DocumentListMode = "full" | "select" | "move";

export type DocumentListBranch =
    | "loading"
    | "error"
    | "empty-search"
    | "empty-filter"
    | "empty-folder"
    | "empty-library"
    | "empty-kb-root"
    | "populated";

export interface UseDocumentListTableOptions {
    rows: ILibraryRow[];
    rowCount: number;
    isLoading: boolean;
    isError: boolean;
    /** The debounced search-bar value. Drives the `empty-search` branch. */
    searchQuery: string;
    /**
     * Type filter currently applied via FilterBar. When non-"all" and the
     * result set is empty, drives the `empty-filter` branch so the empty
     * state can speak specifically to the missing type (scopes / comparisons
     * / documents) rather than the generic library/folder copy.
     */
    typeFilter?: LibraryTypeFilter;
    /** Active folder being viewed, or null at the tab root. */
    folderId: number | null;
    activeTab: LibraryTab;
    mode: DocumentListMode;
    /**
     * Called after a mutating row action (rename, delete, move, etc.) — wired
     * through to every per-type actions menu via the actions column.
     */
    onDocumentAction: () => void;
    /**
     * Move-mode wiring. Only used when `mode === "move"`. The actions column
     * renders a chevron in place of menus; `excludeFolderIds` greys out
     * folders that cannot be the target of the in-flight move.
     */
    onFolderNavigateMove?: (folderId: number) => void;
    excludeFolderIds?: Set<number>;
    /**
     * True when the actions column should be omitted entirely. Mirrors the
     * legacy derivation: `mode === "select" && hideRowActions`.
     */
    hideActionsColumn?: boolean;
    /**
     * Selected single-document highlight (full mode, document detail panel
     * open). Drives the row-level `data-state=selected` styling consumed by
     * the body in IMPL.D.
     */
    selectedDocumentId?: number | null;
    /**
     * Selected single-folder highlight (move mode picker). Same purpose as
     * `selectedDocumentId` but for folder rows.
     */
    selectedFolderId?: number | null;
    /** Drag-and-drop is enabled at the consumer; gates drop-target classes. */
    dragAndDropEnabled?: boolean;
    /**
     * The current hovered drop-target id (from `useLibraryDragAndDrop`). When
     * a row id matches, that row receives the copper drop-target outline.
     */
    hoveredDropTargetId?: string | null;
    /**
     * Unit 2.IMPL.D — controlled table state. Passed straight into
     * `useReactTable.state` so the consumer (DocumentList.tsx) drives
     * pagination/sort/selection externally (and these states bridge to the
     * `useQuery` params via `documentListMappers.ts`).
     *
     * `selectionMode` toggles the leading checkbox column on/off; `"multi"`
     * gets a select-all header + per-row checkboxes, `"single"` (the default)
     * gets none.
     */
    selectionMode?: "multi" | "single";
    pagination: PaginationState;
    onPaginationChange: OnChangeFn<PaginationState>;
    sorting: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
    rowSelection: RowSelectionState;
    onRowSelectionChange: OnChangeFn<RowSelectionState>;
    /**
     * Side-effect to fire BEFORE the select-all header checkbox toggles —
     * see `documentListColumns.tsx` for the cross-page deselect contract.
     */
    onHeaderCheckboxClick?: () => void;
    /** Forwarded to `getDocumentListColumns` — drops the "Client" column on
     *  client-scoped surfaces (see there for the rationale). */
    hideClientColumn?: boolean;
}

export interface UseDocumentListTableResult {
    table: Table<ILibraryRow>;
    columns: ColumnDef<ILibraryRow>[];
    rowClassName: (row: Row<ILibraryRow>) => string;
    isLoading: boolean;
    branch: DocumentListBranch;
    total: number;
}

export function useDocumentListTable(
    opts: UseDocumentListTableOptions
): UseDocumentListTableResult {
    const {
        rows,
        rowCount,
        isLoading,
        isError,
        searchQuery,
        typeFilter = "all",
        folderId,
        activeTab,
        mode,
        onDocumentAction,
        onFolderNavigateMove,
        excludeFolderIds,
        hideActionsColumn = false,
        selectedDocumentId = null,
        selectedFolderId = null,
        dragAndDropEnabled = false,
        hoveredDropTargetId = null,
        selectionMode,
        pagination,
        onPaginationChange,
        sorting,
        onSortingChange,
        rowSelection,
        onRowSelectionChange,
        onHeaderCheckboxClick,
        hideClientColumn = false,
    } = opts;

    // In single-select / move modes, multi-select checkboxes are hidden — the
    // leading checkbox column simply isn't emitted. `useReactTable` still
    // enforces multi-row-selection semantics so cross-page selection state
    // is correct even when the UI hides the per-row checkboxes (which is
    // the legacy MUI behavior we're preserving for ActionsBar dispatch).
    const showCheckboxColumn = mode === "full" && selectionMode === "multi";

    const columns = useMemo(
        () =>
            getDocumentListColumns({
                onDocumentAction,
                hideActions: hideActionsColumn,
                moveMode:
                    mode === "move" && onFolderNavigateMove
                        ? {
                              onFolderNavigate: onFolderNavigateMove,
                              excludeFolderIds,
                          }
                        : undefined,
                selectionMode: showCheckboxColumn ? "multi" : undefined,
                onHeaderCheckboxClick,
                hideClientColumn,
            }),
        [
            onDocumentAction,
            hideActionsColumn,
            mode,
            onFolderNavigateMove,
            excludeFolderIds,
            showCheckboxColumn,
            onHeaderCheckboxClick,
            hideClientColumn,
        ]
    );

    const table = useReactTable<ILibraryRow>({
        data: rows,
        columns,
        rowCount,
        // CRITICAL — without `getRowId` TanStack falls back to row-index
        // ids, which break selection across pagination (Unit 2.RED #14). The
        // helper is the SAME one `useLibrarySelection` uses to key its
        // internal map (Unit 2.IMPL.B), so the table state and the selection
        // store agree on row identity by construction.
        getRowId: (row) => libraryItemRowId(row._raw),
        state: { pagination, sorting, rowSelection },
        onPaginationChange,
        onSortingChange,
        onRowSelectionChange,
        enableRowSelection: (row) =>
            !isForeignSharedSubject(row.original._raw) &&
            (selectionMode !== "single" || row.original.type !== "folder"),
        enableMultiRowSelection: selectionMode !== "single",
        enableColumnResizing: false,
        // Legacy MUI DataGrid is single-column-only; preserve that contract.
        enableMultiSort: false,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const lastPageIndex = Math.max(
        0,
        Math.ceil(rowCount / pagination.pageSize) - 1
    );
    useEffect(() => {
        if (
            rowCount > 0 &&
            rows.length === 0 &&
            !isLoading &&
            !isError &&
            pagination.pageIndex > lastPageIndex
        ) {
            onPaginationChange({ ...pagination, pageIndex: lastPageIndex });
        }
    }, [
        rowCount,
        rows.length,
        isLoading,
        isError,
        pagination,
        lastPageIndex,
        onPaginationChange,
    ]);

    const branch: DocumentListBranch = useMemo(() => {
        if (isError) return "error";
        if (rows.length > 0) return "populated";
        if (isLoading) return "loading";
        if (searchQuery.trim().length > 0) return "empty-search";
        // A type filter narrows the result set; when it yields zero rows,
        // the user wants to know *that the filter is the reason*, not be
        // told "this folder is empty" or "upload your first document".
        // Ordered after empty-search because a search query is the more
        // specific signal (the user just typed it).
        if (typeFilter !== "all") return "empty-filter";
        if (folderId !== null) return "empty-folder";
        if (activeTab === "knowledge_base") return "empty-kb-root";
        return "empty-library";
    }, [
        isError,
        rows.length,
        isLoading,
        searchQuery,
        typeFilter,
        folderId,
        activeTab,
    ]);

    const rowClassName = useMemo(
        () =>
            buildRowClassName({
                mode,
                selectionMode,
                excludeFolderIds,
                selectedDocumentId,
                selectedFolderId,
                dragAndDropEnabled,
                hoveredDropTargetId,
            }),
        [
            mode,
            selectionMode,
            excludeFolderIds,
            selectedDocumentId,
            selectedFolderId,
            dragAndDropEnabled,
            hoveredDropTargetId,
        ]
    );

    return { table, columns, rowClassName, isLoading, branch, total: rowCount };
}

interface BuildRowClassNameOpts {
    mode: DocumentListMode;
    selectionMode?: "multi" | "single";
    excludeFolderIds?: Set<number>;
    selectedDocumentId: number | null;
    selectedFolderId: number | null;
    dragAndDropEnabled: boolean;
    hoveredDropTargetId: string | null;
}

/**
 * Pure factory that returns a `Row<ILibraryRow> => className` helper. The
 * returned function preserves the legacy class set
 * (`library-row--{clickable,inert,excluded,drop-target}`) so any external CSS
 * targeting those hooks keeps working, AND adds Tailwind utilities for the
 * new visuals — most importantly the copper drop-target outline that Unit
 * 2.RED #16d locks in.
 */
function buildRowClassName(
    opts: BuildRowClassNameOpts
): (row: Row<ILibraryRow>) => string {
    const {
        mode,
        selectionMode,
        excludeFolderIds,
        selectedDocumentId,
        selectedFolderId,
        dragAndDropEnabled,
        hoveredDropTargetId,
    } = opts;

    const isSingleSelect = mode === "select" && selectionMode === "single";

    return (row) => {
        const classes: string[] = ["group/row"];

        // Row-level processing tint — a subtle indigo wash on rows whose
        // status is processing/pending so brokers skimming long lists can
        // spot in-progress work without reading each status indicator.
        // Applied BEFORE selection/drop-target so those stronger states win
        // (tailwind-merge keeps the later utility for the same property).
        if (
            row.original.status === "processing" ||
            row.original.status === "pending"
        ) {
            classes.push("bg-primary/[0.025]");
        }

        if (mode === "move") {
            if (row.original.type !== "folder") {
                classes.push(
                    "library-row--inert text-muted-foreground pointer-events-none"
                );
            } else if (excludeFolderIds?.has(row.original._numericId)) {
                classes.push(
                    "library-row--excluded text-muted-foreground cursor-default"
                );
            } else {
                classes.push("library-row--clickable cursor-pointer");
            }
            if (
                row.original.type === "folder" &&
                row.original._numericId === selectedFolderId
            ) {
                classes.push("bg-muted/60");
            }
        } else if (isSingleSelect) {
            // Single-select picker mode: every row is interactive. Folder
            // clicks navigate into the folder tree (useRowClickHandler routes
            // them to `onFolderNavigate`); non-folder clicks toggle the row's
            // TanStack selection. `enableRowSelection` already prevents
            // folders from landing in the selection set, so they're safe to
            // render as clickable — marking them `--inert` here would block
            // the click handler from reaching the navigation path.
            classes.push("library-row--clickable cursor-pointer");
        } else {
            if (
                row.original.type === "folder" ||
                row.original.type === "scope" ||
                row.original.type === "comparison"
            ) {
                classes.push("library-row--clickable cursor-pointer");
            }
            if (
                row.original.type === "document" &&
                row.original._numericId === selectedDocumentId
            ) {
                classes.push("bg-muted/60");
            }
        }

        if (
            dragAndDropEnabled &&
            hoveredDropTargetId !== null &&
            hoveredDropTargetId === row.original.id
        ) {
            classes.push(
                "library-row--drop-target outline outline-2 -outline-offset-2 outline-brand-copper bg-brand-copper/5"
            );
        }

        return classes.join(" ");
    };
}
